import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CurrencyInr } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { cn } from '@/lib/cn'
import { istDateKey } from '@/lib/ist'
import { Input } from '@/ui'
import { errorMessage } from '../../api/core'
import { useExceptions } from '../../api/exceptions'
import {
  maskAccount,
  useBankAccounts,
  usePayAgain,
  usePayout,
  type PayAgainRoute,
  type PayoutDetail,
} from '../../api/payouts'
import { uploadEvidence } from '../../api/reference'
import { ActionButton } from '../../components/Can'
import { QueryState } from '../../components/Layout'
import { Money, UserName } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  type BoardColumn,
} from '../../components/board'
import {
  TRY_ROUTE_LABEL,
  boardDate,
  boardWhen,
  failureIsAboutAccount,
  hoursSinceIso,
  isFailed,
  liveTry,
  nextTryKey,
  payoutPill,
} from '../../model/payoutBoard'
import { PAYOUT_STATUS_LABEL } from '../../api/payouts'
import { FarmerCode } from './shared'
import { useScope } from '../../api/appConfig'

type TryRow = {
  key: string
  attemptNo: number
  route: string
  ref: string
  sent: string
  result: ReactNode
}

const ROUTE_LABEL: Record<PayAgainRoute, string> = {
  SAME_ACCOUNT: 'Same account',
  NEW_ACCOUNT: 'New checked account',
  BULK_FILE: 'Bulk file',
  MANUAL: 'Manual transfer',
}

/**
 * Pay again · <lot> · <farmer> (alignment map V1-F4-B4b PayAgain; F4-B4.b): pick one route — the
 * same account (off when the bank's reason is about his account), his new checked account, or a
 * manual transfer with the bank's reference that a second person confirms — see every try so far,
 * and pay the failed try's amount (never typed) under a new reference.
 */
export function PayAgainPage() {
  const { id } = useParams()
  const q = usePayout(id)
  return (
    <QueryState query={q} label="the payment">
      {q.data && <PayAgain p={q.data} />}
    </QueryState>
  )
}

function PayAgain({ p }: { p: PayoutDetail }) {
  const { v1 } = useScope()
  const { user } = useAuth()
  const ex = useExceptions(
    {
      type: 'PAYMENT_FAILED',
      status: 'OPEN',
      farmerId: p.farmerId ?? undefined,
      page: 1,
      limit: 50,
    },
    Boolean(p.farmerId),
  )
  const accounts = useBankAccounts(p.farmerId ?? undefined)
  const failure =
    (ex.data?.rows ?? []).find(
      (e) => e.detail.unit_id === p.unitId && (e.detail.kind ?? 'LOT') === p.kind,
    ) ?? null
  const failures =
    failure && Array.isArray(failure.detail.failures)
      ? (failure.detail.failures as Array<Record<string, unknown>>)
      : []
  const reasonClass = (failures[failures.length - 1]?.reason_class ??
    failure?.detail.reason_class) as string | undefined
  const aboutAccount = failureIsAboutAccount(p.failureReason, reasonClass)
  const primary = (accounts.data ?? []).find((a) => a.isPrimary && a.status === 'VERIFIED') ?? null
  const newAccount = primary && primary.id !== p.accountId ? primary : null

  const failed = isFailed(p.status)
  const live = liveTry(p.tries)
  const nextAttempt = Math.max(p.attemptNo, ...p.tries.map((t) => t.attemptNo)) + 1
  const defaultRoute: PayAgainRoute | null = aboutAccount
    ? newAccount
      ? 'NEW_ACCOUNT'
      : null
    : 'SAME_ACCOUNT'
  const [picked, setPicked] = useState<PayAgainRoute | null>(null)
  const route = picked ?? defaultRoute
  const [utr, setUtr] = useState('')
  const [paidOn, setPaidOn] = useState(istDateKey())
  const [proof, setProof] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const payAgain = usePayAgain()
  const utrOk = /^[A-Za-z0-9]{6,32}$/.test(utr.trim())

  const openedAt = failure?.raisedAt ?? p.processedAt ?? p.requestedAt
  const openH = hoursSinceIso(openedAt)
  const critical = failure?.severity === 'CRITICAL' || (openH ?? 0) >= 24

  const sameOff = aboutAccount
    ? `Off: the bank’s reason is about his account (${p.failureReason ?? 'no reason given'}).`
    : null
  const routeOff: Record<PayAgainRoute, string | null> = {
    SAME_ACCOUNT: sameOff,
    NEW_ACCOUNT: newAccount
      ? null
      : 'No new checked account yet: fix his bank details first (₹1 check and a second approval).',
    BULK_FILE: null,
    MANUAL: null,
  }
  const toAccount =
    route === 'NEW_ACCOUNT' && newAccount
      ? `${maskAccount(newAccount.accountLast4).replace(' ', '')} · ${newAccount.ifsc ?? '—'}`
      : route === 'MANUAL'
        ? 'Paid by hand from the bank'
        : route === 'BULK_FILE' && primary
          ? `${maskAccount(primary.accountLast4).replace(' ', '')} · ${primary.ifsc ?? '—'} · next bulk file`
          : `${maskAccount(p.accountLast4).replace(' ', '')} · ${p.accountIfsc ?? '—'}`
  const nextKey = nextTryKey(p.idempotencyKey, nextAttempt)

  const blocked = !failed
    ? `This try is ${(PAYOUT_STATUS_LABEL[p.status] ?? p.status).toLowerCase()}: only a failed or returned try is paid again.`
    : live
      ? `Try ${live.attemptNo} is ${(PAYOUT_STATUS_LABEL[live.status] ?? live.status).toLowerCase()}: wait for it.`
      : !route
        ? 'Pick a route first.'
        : routeOff[route]
          ? routeOff[route]
          : route === 'MANUAL' && (!utrOk || !paidOn)
            ? 'Type the bank reference (UTR, 6–32 letters or digits) and the day it was paid.'
            : null

  const pay = async () => {
    if (!route || blocked) return
    try {
      await payAgain.mutateAsync({
        id: p.id,
        route,
        ...(route === 'MANUAL'
          ? { utr: utr.trim(), paidOn, proofMediaId: proof ?? undefined }
          : {}),
      })
      setUtr('')
      setProof(null)
    } catch {
      /* toasted */
    }
  }

  const rows: TryRow[] = p.tries.map((t) => {
    const pill = payoutPill({
      status: t.status,
      requestedAt: t.requestedAt,
      blockers: [],
      mode: t.mode,
    })
    const when = t.processedAt ? ` ${boardDate(t.processedAt)}` : ''
    return {
      key: t.id,
      attemptNo: t.attemptNo,
      route: t.mode
        ? (TRY_ROUTE_LABEL[t.mode] ?? t.mode)
        : t.id === p.id
          ? `Account ${maskAccount(p.accountLast4).replace(' ', '')}`
          : '—',
      ref: t.idempotencyKey ?? '—',
      sent:
        t.requestedAt && t.status !== 'PENDING_APPROVAL' && t.status !== 'READY'
          ? boardWhen(t.requestedAt)
          : '—',
      result: (
        <Pill tone={pill.tone}>
          {pill.text}
          {(t.status === 'PROCESSED' || isFailed(t.status)) && when}
          {t.utr ? ` · UTR ${t.utr}` : ''}
        </Pill>
      ),
    }
  })
  if (failed && !live)
    rows.push({
      key: 'next',
      attemptNo: nextAttempt,
      route: route
        ? `${ROUTE_LABEL[route]}${route === 'NEW_ACCOUNT' && newAccount ? ` ${maskAccount(newAccount.accountLast4).replace(' ', '')}` : ''}`
        : '—',
      ref: nextKey ?? 'set when sent',
      sent: '—',
      result: <Pill tone="idle">Not sent yet</Pill>,
    })
  const columns: BoardColumn<TryRow>[] = [
    { id: 'try', header: 'Try', cell: (r) => r.attemptNo },
    { id: 'route', header: 'Route', cell: (r) => r.route },
    { id: 'ref', header: 'Our reference', cell: (r) => r.ref, hideBelow: 'sm' },
    { id: 'sent', header: 'Sent', cell: (r) => r.sent, hideBelow: 'sm' },
    { id: 'result', header: 'Result', cell: (r) => r.result },
  ]

  return (
    <BoardPage
      title={`Pay again · ${p.unitCode} · ${p.farmerName ?? 'Farmer'}`}
      actions={
        <LinkButton to={`/c/payouts/failed?open=${p.id}`} icon={<ArrowLeft aria-hidden="true" />}>
          Back to the failure
        </LinkButton>
      }
    >
      <BoardColumns
        main={
          <>
            <fieldset className="flex flex-col gap-2.5">
              <legend className="mb-2 text-[16px] font-bold">Route · pick one</legend>
              <RouteCard
                checked={route === 'SAME_ACCOUNT'}
                onPick={() => setPicked('SAME_ACCOUNT')}
                off={routeOff.SAME_ACCOUNT}
                title={`Same account · ${maskAccount(p.accountLast4).replace(' ', '')} · ${p.accountIfsc ?? '—'}`}
                hint={sameOff ?? 'Worth trying again: the bank’s reason was not about his account.'}
              />
              <RouteCard
                checked={route === 'NEW_ACCOUNT'}
                onPick={() => setPicked('NEW_ACCOUNT')}
                off={routeOff.NEW_ACCOUNT}
                title={
                  newAccount
                    ? `New checked account · ${maskAccount(newAccount.accountLast4).replace(' ', '')} · ${newAccount.ifsc ?? '—'}`
                    : 'New checked account'
                }
                hint={
                  newAccount ? (
                    <>
                      {newAccount.nameAtBank ? `${newAccount.nameAtBank} · ` : ''}OnGrid verification{' '}
                      {boardWhen(newAccount.verifiedAt)} · approved by{' '}
                      <UserName id={newAccount.approvedById} /> (F4-B4.c)
                    </>
                  ) : (
                    <>
                      {routeOff.NEW_ACCOUNT}{' '}
                      {p.farmerId && (
                        <Link
                          className="underline"
                          to={`/c/farmers/${p.farmerId}/bank?payout=${p.id}`}
                        >
                          Fix bank details
                        </Link>
                      )}
                    </>
                  )
                }
              />
              <RouteCard
                checked={route === 'MANUAL'}
                onPick={() => setPicked('MANUAL')}
                title="Manual transfer"
                hint="Only if RazorpayX cannot pay at all: pay from the bank, then type its bank reference."
              >
                {route === 'MANUAL' && (
                  <div className="mt-2 flex flex-col gap-2 text-[14px]">
                    <label className="flex flex-wrap items-center gap-2">
                      <span className="w-40 text-muted">Bank reference (UTR)</span>
                      <Input
                        value={utr}
                        onChange={(e) => setUtr(e.target.value)}
                        autoComplete="off"
                        className="h-[34px] max-w-[220px]"
                        aria-invalid={utr !== '' && !utrOk}
                      />
                    </label>
                    <label className="flex flex-wrap items-center gap-2">
                      <span className="w-40 text-muted">Paid on</span>
                      <Input
                        type="date"
                        value={paidOn}
                        onChange={(e) => setPaidOn(e.target.value)}
                        className="h-[34px] max-w-[220px]"
                      />
                    </label>
                    <label className="flex flex-wrap items-center gap-2">
                      <span className="w-40 text-muted">Proof (optional)</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="text-[13px]"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          setUploading(true)
                          setUploadError(null)
                          try {
                            setProof(await uploadEvidence(file))
                          } catch (err) {
                            setProof(null)
                            setUploadError(errorMessage(err))
                          } finally {
                            setUploading(false)
                          }
                        }}
                      />
                    </label>
                    {uploadError && (
                      <span className="text-danger">Photo not uploaded: {uploadError}</span>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="w-40 text-muted">Confirmed by</span>
                      <span>— a second person, not the one who typed the UTR</span>
                      <ActionButton
                        size="sm"
                        variant="secondary"
                        disabled
                        title="After Pay, a second person confirms it from Payouts (Confirm bank transfer)."
                      >
                        Confirm
                      </ActionButton>
                    </div>
                  </div>
                )}
              </RouteCard>
              {!v1 && (
                <RouteCard
                  checked={route === 'BULK_FILE'}
                  onPick={() => setPicked('BULK_FILE')}
                  title="Bulk file"
                  hint="Goes out in the next bulk upload file from Payouts."
                />
              )}
            </fieldset>
            <BoardTable<TryRow>
              caption="Tries"
              columns={columns}
              rows={rows}
              getRowId={(r) => r.key}
            />
          </>
        }
        side={
          <SidePanel
            label="This try"
            title={`Try ${live ? live.attemptNo : nextAttempt}`}
            sub={
              <>
                <FarmerCode id={p.farmerId} /> · {p.villageName ?? '—'} ·{' '}
                {p.kind === 'TOP_UP' ? 'top-up' : 'lot payment'}
              </>
            }
          >
            {failed ? (
              <Pill tone="bad">
                {p.status === 'REVERSED' ? 'Returned' : 'Failed'}
                {openH !== null ? ` · open ${openH} h` : ''}
                {critical ? ' · critical' : ''}
              </Pill>
            ) : (
              <Pill tone={payoutPill(p).tone}>{payoutPill(p).text}</Pill>
            )}
            <Facts>
              <FactRow label="Amount" strong>
                <Money paise={p.amountPaise} className="font-sans" />
              </FactRow>
              <FactRow>
                <Note>the failed try’s amount · never typed</Note>
              </FactRow>
              <FactRow label="Try number">{live ? live.attemptNo : nextAttempt}</FactRow>
              <FactRow label="Our reference">{nextKey ?? 'set when sent'}</FactRow>
              <FactRow label="Route">{route ? ROUTE_LABEL[route] : '—'}</FactRow>
              {route && user && (
                <FactRow>
                  <Note>
                    picked by you, <UserName id={user.id} />
                  </Note>
                </FactRow>
              )}
              <FactRow label="To">{toAccount}</FactRow>
            </Facts>
            {live ? (
              <Pill tone="bad" wrap>
                Try {live.attemptNo} is{' '}
                {(PAYOUT_STATUS_LABEL[live.status] ?? live.status).toLowerCase()}
              </Pill>
            ) : (
              <Pill tone="ok">No other try waiting or in progress</Pill>
            )}
            <Note>
              Each try has its own reference, so the lot cannot be paid twice. A second Pay, or a
              second person on this lot, is refused while a try is waiting or in progress.
            </Note>
            <Note>
              If this try fails too, the same failure opens again. Payout link or cash: not used in
              October.
            </Note>
            <div className="flex-1" />
            <span className="text-[13px] text-ink">
              Then check it shows Paid with a bank reference and the SMS has gone.
            </span>
            <PanelActions>
              <ActionButton
                perm="payouts:pay-again"
                leadingIcon={<CurrencyInr />}
                blockedReason={blocked}
                loading={payAgain.isPending || uploading}
                onClick={() => void pay()}
              >
                Pay <Money paise={p.amountPaise} className="font-sans" />
              </ActionButton>
              {blocked && <Note>{blocked}</Note>}
            </PanelActions>
          </SidePanel>
        }
      />
    </BoardPage>
  )
}

function RouteCard({
  checked,
  onPick,
  off,
  title,
  hint,
  children,
}: {
  checked: boolean
  onPick: () => void
  off?: string | null
  title: ReactNode
  hint: ReactNode
  children?: ReactNode
}) {
  return (
    <div
      onClick={off || checked ? undefined : onPick}
      className={cn(
        'flex items-start gap-3 rounded-card bg-surface px-3.5 py-2.5',
        !off && !checked && 'cursor-pointer',
        checked ? 'border-2 border-ink' : 'border-[1.5px] border-[#8a827a]',
        off && 'opacity-80',
      )}
    >
      <input
        type="radio"
        name="pay-again-route"
        checked={checked}
        disabled={Boolean(off)}
        onChange={onPick}
        className="mt-1 h-5 w-5 shrink-0 accent-ink"
        aria-label={typeof title === 'string' ? title : undefined}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <b className={cn('text-[16px]', off && 'text-muted')}>{title}</b>
        <span className={cn('text-[14px]', checked ? 'text-ink' : 'text-muted')}>{hint}</span>
        {children}
      </div>
    </div>
  )
}
